import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { ChatReportSpec } from '../../models/chat.models';
import { TimezoneService } from 'src/app/core/utils/timezone.service';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

@Component({
  selector: 'app-chat-financial-report',
  templateUrl: './chat-financial-report.component.html',
  styleUrls: ['./chat-financial-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatFinancialReportComponent implements OnChanges {
  @Input({ required: true }) spec!: string;

  report: ChatReportSpec | null = null;
  fallbackRaw: string | null = null;
  downloading = false;

  constructor(
    private readonly timezoneService: TimezoneService,
    private readonly dateFormatLocalization: DateFormatLocalizationService,
  ) {}

  ngOnChanges(): void {
    try {
      const parsed = JSON.parse(this.spec) as ChatReportSpec;
      if (!parsed?.columns?.length || !Array.isArray(parsed.rows)) {
        this.fallbackRaw = this.spec;
        return;
      }
      this.report = parsed;
      this.fallbackRaw = null;
    } catch {
      this.fallbackRaw = this.spec;
    }
  }

  downloadCsv(): void {
    if (!this.report) return;
    const rows = [this.report.columns, ...this.report.rows];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const bom = '\uFEFF'; // UTF-8 BOM for Excel
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.sanitizeName(this.report.title)}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async downloadPdf(): Promise<void> {
    if (!this.report || this.downloading) return;
    this.downloading = true;
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const margin = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let y = margin;

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text(this.report.title, margin, y);
      y += 7;

      // Subtitle
      if (this.report.subtitle) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(this.report.subtitle, margin, y);
        y += 6;
      }

      // Date
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      const country = this.dateFormatLocalization.getCurrentCountryCode();
      doc.text(`Generado: ${this.timezoneService.formatDateForCenter(new Date(), country)}`, margin, y);
      y += 6;

      // Divider
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      // Calculate column widths
      const cols = this.report.columns;
      const colWidth = (pageWidth - margin * 2) / cols.length;
      const rowHeight = 7;
      const headerHeight = 8;

      // Header row
      doc.setFillColor(26, 26, 46);
      doc.rect(margin, y, pageWidth - margin * 2, headerHeight, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      cols.forEach((col, i) => {
        doc.text(col, margin + i * colWidth + 2, y + 5.5, { maxWidth: colWidth - 4 });
      });
      y += headerHeight;

      // Data rows
      doc.setFont('helvetica', 'normal');
      this.report.rows.forEach((row, rowIdx) => {
        if (y + rowHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        if (rowIdx % 2 === 0) {
          doc.setFillColor(248, 249, 250);
          doc.rect(margin, y, pageWidth - margin * 2, rowHeight, 'F');
        }
        doc.setFontSize(8);
        doc.setTextColor(30, 30, 30);
        row.forEach((cell, i) => {
          doc.text(String(cell ?? ''), margin + i * colWidth + 2, y + 4.8, { maxWidth: colWidth - 4 });
        });
        // Row border
        doc.setDrawColor(224, 224, 224);
        doc.line(margin, y + rowHeight, pageWidth - margin, y + rowHeight);
        y += rowHeight;
      });

      // Summary
      if (this.report.summary) {
        y += 6;
        if (y + 14 > pageHeight - margin) { doc.addPage(); y = margin; }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(80, 80, 80);
        const lines = doc.splitTextToSize(this.report.summary, pageWidth - margin * 2);
        doc.text(lines, margin, y);
        y += lines.length * 4.5;
      }

      // Footer
      const total = doc.getNumberOfPages();
      for (let p = 1; p <= total; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setTextColor(180, 180, 180);
        doc.text(`Cafler CASH — ${this.report.title} — Página ${p}/${total}`, margin, pageHeight - 6);
      }

      doc.save(`${this.sanitizeName(this.report.title)}-${Date.now()}.pdf`);
    } finally {
      this.downloading = false;
    }
  }

  private sanitizeName(name: string): string {
    return name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '-').toLowerCase().slice(0, 60);
  }
}
